import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useEffect, useState } from 'react';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import { UserSummaryType } from '../../api/types/user/user-summary.type';
import { findUserOperation } from './operations/find-user-operation';
import CancelButton from '../../component/ui/buttons/cancel-button/cancel-button';
import ApproveButtons from '../../component/ui/buttons/approve-button/approve-buttons';
import { SearchUserType } from './types/search-user-type';
import { Chip } from 'primereact/chip';
import StudentIcon from '../../assets/icons/search/student-icon.svg';
import LecturerIcon from '../../assets/icons/search/lecturer-icon.svg';
import AdministrationIcon from '../../assets/icons/search/administration-icon.svg';
import { UserType } from '../../api/types/user/user-type';
import phoneNumberUtils, {
  internationalToIsraeli,
} from '../../utils/international-to-israeli';

export interface FindUserModel {
  onApprove: (
    selectedUser: UserSummaryType[],
    setLoading: (loading: boolean) => void,
  ) => void;
  displayTable?: boolean;
  onUserSelected?: (user: UserSummaryType) => void;
  multiple?: boolean;
  role?: UserType;
  onChange?: (selectedUser: UserSummaryType[]) => any;
  displayApprove?: boolean;
}

export const FindUser: React.FC<FindUserModel> = ({
  onApprove,
  displayTable = true,
  onUserSelected,
  multiple = true,
  role,
  onChange,
  displayApprove = true,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<UserSummaryType[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<SearchUserType[]>([]);

  const [loading, setLoading] = useState(false);

  const search = async (event: AutoCompleteCompleteEvent) => {
    await findUserOperation(event, setFilteredUsers, setLoading, role);
  };

  useEffect(() => {
    console.log(`The filterdUser: ${JSON.stringify(filteredUsers)}`);
  }, [filteredUsers]);

  const handleOnApprove = async () => {
    onApprove(selectedUsers, setLoading);
  };

  const selectedUser = (event: AutoCompleteSelectEvent) => {
    const user: UserSummaryType = event.value;
    onUserSelected?.(user);
  };

  const getIconByUserRole = (userRole: UserType) => {
    switch (userRole) {
      case UserType.ADMINISTRATION:
        return AdministrationIcon;
      case UserType.LECTURER:
        return LecturerIcon;
      case UserType.STUDENT:
        return StudentIcon;
    }
  };

  const handleOnSelectUsers = (users: UserSummaryType[]) => {
    setSelectedUsers(users);
    onChange?.(users);
  };

  return (
    <div>
      <div className="card p-fluid">
        <AutoComplete
          field={'label'}
          itemTemplate={(suggestion: UserSummaryType, index: number) => {
            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ marginRight: '4px' }}>
                  {suggestion.firstName} {suggestion.lastName}{' '}
                  {suggestion.nationalId}
                </div>
                <img src={getIconByUserRole(suggestion.role)} height={18} />
              </div>
            );
          }}
          placeholder={'חפש משתמש לפי שם, שם משפחה, ת.ז, טלפון...'}
          multiple={multiple}
          value={selectedUsers}
          suggestions={filteredUsers}
          onSelect={selectedUser}
          completeMethod={search}
          onChange={(e) => handleOnSelectUsers(e.value)}
          style={{ direction: 'ltr' }}
          inputStyle={{
            paddingRight: '32px',
            direction: 'rtl',
          }}
        />
      </div>

      {displayTable && (
        <div>
          <DataTable
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            selectionMode={'multiple'}
            multiple={true}
            groupRowsBy="dsf"
            value={selectedUsers}
            selection={selectedUsers}
            onSelectionChange={(e) => {
              handleOnSelectUsers(e.value as UserSummaryType[]);
            }}
            dataKey="id"
            tableStyle={{
              maxWidth: '780px',
              direction: 'rtl',
              marginTop: '16px',
            }}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
            <Column
              style={{ textAlign: 'right', width: '3rem' }}
              field="firstName"
              header="שם"
            />
            <Column field="lastName" header="" style={{ textAlign: 'right' }} />
            <Column
              field="nationalId"
              header="ת.ז"
              style={{ textAlign: 'right' }}
            />
            <Column
              field="phoneNumber"
              header="טלפון נייד"
              style={{ textAlign: 'right' }}
              body={(rowData: UserSummaryType) => (
                <div>
                  {phoneNumberUtils.internationalToIsraeli(rowData.phoneNumber)}
                </div>
              )}
            />
            <Column
              field="dateOfBirth1"
              header="תאריך לידה"
              style={{ textAlign: 'right' }}
              body={(rowData: UserSummaryType) =>
                rowData.dateOfBirth
                  ? new Date(rowData.dateOfBirth).toLocaleDateString('he-IL') // Format date to 'dd/mm/yyyy' for Hebrew locale
                  : ''
              }
            />
          </DataTable>

          <div
            style={{
              width: '600px',
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {selectedUsers &&
              selectedUsers.map((selectedUserItem, index) => (
                <Chip
                  key={selectedUserItem.id} // Adding a key for each Chip
                  label={`${selectedUserItem.firstName} ${selectedUserItem.lastName}`}
                  removable={true}
                  onRemove={() => {
                    // Filter out the selected item and update the state
                    setSelectedUsers(
                      selectedUsers.filter(
                        (_, courseItemIndex) => courseItemIndex !== index,
                      ),
                    );
                  }}
                />
              ))}
          </div>

          {displayApprove && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'left',
                marginTop: '20px',
                paddingBottom: '10px',
                width: '100%',
              }}
            >
              <CancelButton
                onClick={() => {
                  console.log();
                }}
              />

              <div style={{ width: '10px' }} />
              <ApproveButtons
                isLoading={loading}
                onClick={async () => {
                  await handleOnApprove();
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
